"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { PortalCtaButton } from "@/components/landing/portal-cta-button";
import { useAppScroll } from "@/components/providers/locomotive-scroll-provider";
import { Logo } from "@/components/brand/logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { useAnalytics } from "@/hooks/use-analytics";
import { APP_URL } from "@/lib/links";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { label: "What is moongate?", href: "/" },
  { label: "Developers", href: "/developers" },
];

const TRADE_URL = APP_URL;

export function Navbar() {
  const { subscribe } = useAppScroll();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const analytics = useAnalytics();

  useEffect(() => subscribe((v) => setScrolled(v.scroll > 12)), [subscribe]);

  // "What is moongate?" (/) is only active on the exact home route; other
  // links (e.g. /developers) match by prefix. The active link is filled in
  // and rendered in the foreground (white) so the current page reads as selected.
  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  const linkClass = (href: string, base: string) =>
    cn(
      base,
      isActive(href)
        ? "bg-muted/60 text-foreground"
        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
    );

  return (
    <header className="pointer-events-none fixed inset-x-0 top-0 z-[200] flex justify-center">
      <motion.div
        initial={false}
        animate={{
          width: scrolled ? "min(64rem, 94%)" : "100%",
          marginTop: scrolled ? 12 : 0,
        }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "pointer-events-auto",
          scrolled
            ? "rounded-full border border-border/60 bg-background/90 shadow-glow md:bg-background/70 md:backdrop-blur-xl"
            : "border-b border-transparent",
        )}
      >
        <nav className="flex h-14 items-center justify-between gap-4 px-4 sm:px-6">
          <Logo size={24} onClick={() => analytics.trackButton("Logo", "Navbar")} />

          <div className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                aria-current={isActive(link.href) ? "page" : undefined}
                className={linkClass(link.href, "rounded-full px-3 py-1.5 text-sm transition-colors")}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <PortalCtaButton
              size="sm"
              className="hidden md:inline-flex"
              onClick={() => analytics.trackButton("Trade Now", "Navbar CTA", { button_url: TRADE_URL })}
            />
            <button
              className="grid size-9 place-items-center rounded-full border border-border text-foreground md:hidden"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        </nav>
      </motion.div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="pointer-events-auto fixed inset-0 bg-background/60 backdrop-blur-sm md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.div
              className="pointer-events-auto fixed inset-x-3 top-3 z-10 rounded-2xl border border-border/60 bg-card/90 p-4 backdrop-blur-xl md:hidden"
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="mb-3 flex items-center justify-between">
                <Logo size={22} animated={false} />
                <button
                  onClick={() => setOpen(false)}
                  className="grid size-8 place-items-center rounded-full border border-border"
                  aria-label="Close menu"
                >
                  <X className="size-4" />
                </button>
              </div>
              <div className="flex flex-col">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setOpen(false)}
                    aria-current={isActive(link.href) ? "page" : undefined}
                    className={linkClass(link.href, "rounded-lg px-3 py-2.5 text-left text-sm transition-colors")}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              <PortalCtaButton
                size="default"
                className="mt-3 w-full"
                onClick={() => {
                  analytics.trackButton("Trade Now", "Navbar CTA", { button_url: TRADE_URL });
                  setOpen(false);
                }}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
